import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Button, FormControl, Grid } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUnidadeSelecionada } from "../../../../features/usuario-logado/usuarioLogadoSlice";
import ErrorBadge from "../../../common/components/forms/errorBadge";
import FormHeaderMedium from "../../../common/components/forms/formHeaderMedium";
import LoadingIndicator from "../../../common/components/loadingIndicator";
import { useUnidadeNegocioGuard } from "../../../common/hooks/useUnidadeNegocioGuard";
import PageLayout from "../../../common/layouts/pageLayout";
import { ClientIdAndDescriptionDTO } from "../../../common/types/clientData";
import { FollowUpDto } from "../../../common/types/followUp";
import { KEYCLOAK_TOKEN_TIMEOUT } from "../../../../App";
import { setErrorFeedback, setSuccessFeedback } from "../../../../features/feedback/feedbackSlice";
import { fetchClientesWithFilterDto } from "../../../../features/cliente/clienteAPI";
import { fetchByFilterDto } from "../../../../features/follow-up/followUpAPI";
import { downloadZipFile, generateBatchFileTxtZip, GenerateBatchPayload } from "./martinsLog.hooks";
import MultiSelectStyled, { SimpleMultiSelectOption } from "../../../common/components/input/multiSelectStyled";
import { Field, Form } from "react-final-form";
import QCXFinalDatePickerField from "../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField";

const MartinsLogEdiSaidaPage = () => {
  useUnidadeNegocioGuard();
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;
  const selectedUnidade: number = useSelector(selectUnidadeSelecionada);

  const [error, setError] = useState<string | string[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [clientes, setClientes] = useState<ClientIdAndDescriptionDTO[] | undefined>(undefined);
  const [followUpsData, setFollowUpsData] = useState<FollowUpDto[] | undefined>(undefined);

  const [selectedClientes, setSelectedClientes] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedProcessos, setSelectedProcessos] = useState<SimpleMultiSelectOption[]>([]);
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  const url = `${process.env.REACT_APP_QCX_API_BASE_URL!}/${process.env.REACT_APP_QCX_API_FATURA_ENDPOINT!}/saida-edi`;

  const processosOptions = useMemo(() => {
    if (!followUpsData || selectedClientes.length === 0) return [];

    const clientIds = selectedClientes.map((c) => Number(c.id));
    const processosFiltered = followUpsData.filter((followUp: FollowUpDto) =>
      followUp.importador?.id ? clientIds.includes(followUp.importador.id) : false
    );

    const processosUnicos = Array.from(new Set(processosFiltered.map((f) => f.numero)));

    return processosUnicos.map((numero) => ({
      id: numero,
      value: numero,
    }));
  }, [followUpsData, selectedClientes]);

  const clearData = () => {
    setSelectedClientes([]);
    setSelectedProcessos([]);
    setDataInicio('');
    setDataFim('');
    setIsLoading(false);
  };

  const hasAnyFilter = selectedClientes.length > 0 || selectedProcessos.length > 0 || !!dataInicio || !!dataFim;
  const canSubmit = hasAnyFilter && !isLoading && !error;

  const handleSubmit = async () => {
    if (hasAnyFilter && token && selectedUnidade) {
      keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);
      setIsLoading(true);

      try {
        const payload: GenerateBatchPayload = {
          processos: selectedProcessos.map(p => p.id.toString()),
          clientes: selectedClientes.map(c => c.id.toString()),
          origem: "",
          dataInicio,
          dataFim
        };

        const targetUrl = `${url}/gerar-lote/zip`;
        const response = await generateBatchFileTxtZip(token, selectedUnidade.toString(), targetUrl, payload);

        if (response.statusCode === 200 && response.file) {
          downloadZipFile(response.file, `MARTINS_US_858_LOTE_${new Date().getTime()}`);
          dispatch(setSuccessFeedback({ message: "Lote 858 Zipado gerado com sucesso!" }));
          clearData();
        } else {
          dispatch(setErrorFeedback({ message: response.message || "Erro ao gerar arquivo." }));
        }
      } catch (err) {
        dispatch(setErrorFeedback({ message: "Erro inesperado na comunicação com o servidor." }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getClientsAndFollowUps = async () => {
    try {
      const { data } = await fetchClientesWithFilterDto([{ name: 'filterDadosMinimos', value: 'TRUE' }]);
      setClientes(data);
    } catch (error) {
      dispatch(setErrorFeedback({ message: 'Erro ao obter clientes' }));
    }

    try {
      const { data } = await fetchByFilterDto([{ name: 'filterDadosMinimos', value: 'TRUE' }]);
      setFollowUpsData(data);
    } catch (error) {
      dispatch(setErrorFeedback({ message: 'Erro ao obter FollowUps' }));
    }
  };

  useEffect(() => {
    getClientsAndFollowUps();
  }, []);

  return (
    <PageLayout title={"Saida EDI 858"} icon={<InsertDriveFileIcon color={"secondary"} />}>
      {isLoading ? (
        <LoadingIndicator message={"Gerando Lote Zipado 858..."} />
      ) : (
        <Form onSubmit={() => { }}>
          {() => (
            <form>
              <Grid container spacing={2}>
                <FormHeaderMedium>Selecione as informações para gerar o arquivo 858 em lote</FormHeaderMedium>

                <Grid item sm={6}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      options={(clientes || []).map((cliente) => ({
                        id: cliente.id?.toString() || '',
                        value: cliente.description + " - " + cliente.id,
                      }))}
                      controlledValues={selectedClientes}
                      onChangeAction={(e, val) => {
                        setSelectedClientes(val || []);
                        setSelectedProcessos([]);
                      }}
                      label={"Clientes"}
                    />
                  </FormControl>
                </Grid>

                <Grid item sm={6}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      options={processosOptions}
                      controlledValues={selectedProcessos}
                      onChangeAction={(e, val) => setSelectedProcessos(val || [])}
                      label={"Processos"}
                      disabled={selectedClientes.length === 0}
                      placeholder={selectedClientes.length === 0 ? "Selecione um Cliente primeiro" : "Selecionar Processos"}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <Field
                    name="dataInicioRegistro"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="data-inicio-registro"
                        name={input.name}
                        label={'Data Início Registro'}
                        required={false}
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(date: any) => {
                          setDataInicio(date ? date.toISOString() : '');
                          input.onChange(date);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    name="dataFimRegistro"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="data-fim-registro"
                        name={input.name}
                        label={'Data Fim Registro'}
                        required={false}
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(date: any) => {
                          setDataFim(date ? date.toISOString() : '');
                          input.onChange(date);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>

                <ErrorBadge error={error} />

                <Grid item sm={12} textAlign={"right"} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                  >
                    Gerar Lote (ZIP)
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      )}
    </PageLayout>
  );
};

export default MartinsLogEdiSaidaPage;
